import React from 'react';
import { Globe, CheckCircle, MapPin, Users, FileText, TrendingUp } from 'lucide-react';

export default function NewMarketEntry() {
  const framework = [
    {
      phase: 'Phase 1: Market Intelligence & Feasibility',
      duration: '2-3 weeks',
      activities: [
        'Market size and growth trajectory analysis',
        'Regulatory landscape mapping and compliance requirements',
        'Competitive intelligence and white space identification',
        'Distribution channel assessment (online, retail, B2B)',
        'Go/no-go recommendation with risk assessment'
      ]
    },
    {
      phase: 'Phase 2: Entry Strategy Development',
      duration: '3-4 weeks',
      activities: [
        'Market entry model selection (direct, partnership, JV)',
        'Pricing strategy for local market dynamics',
        'Product-market fit validation and localization needs',
        'Partner identification and preliminary outreach',
        'Launch timeline and resource allocation planning'
      ]
    },
    {
      phase: 'Phase 3: Operational Setup',
      duration: '6-8 weeks',
      activities: [
        'Legal entity formation and business registration',
        'Banking, payments, and financial infrastructure',
        'Supply chain and logistics configuration',
        'E-commerce platform setup and localization',
        'Customer service and support infrastructure'
      ]
    },
    {
      phase: 'Phase 4: Market Launch & Scale',
      duration: 'Ongoing',
      activities: [
        'Soft launch with pilot customer segment',
        'Marketing campaign execution and optimization',
        'Sales channel activation and partner onboarding',
        'Performance monitoring and rapid iteration',
        'Scale strategy based on validated learnings'
      ]
    }
  ];

  const differentiators = [
    {
      title: 'The 90-Day Market Entry Sprint',
      icon: TrendingUp,
      description: 'Most consultants deliver reports. We deliver operational market presence in 90 days—from feasibility analysis to first customer orders. Speed matters in market windows.'
    },
    {
      title: 'On-Ground Local Expertise',
      icon: MapPin,
      description: 'We maintain local teams in key markets who understand cultural nuances, regulatory requirements, and buyer behaviors that outsiders miss. Real boots on the ground, not remote consultants.'
    },
    {
      title: 'Risk-Mitigated Entry Models',
      icon: FileText,
      description: 'We structure entries to minimize upfront capital and operational risk. Test, learn, and scale—not bet-the-company launches. Flexible models that adapt as you learn the market.'
    },
    {
      title: 'End-to-End Execution Support',
      icon: Users,
      description: 'From entity formation to first sale, we handle execution—not just strategy. Legal setup, banking, logistics, hiring, and operational infrastructure. We make market entry happen.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Globe size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">
                New Market Entry
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                Strategic market entry services for new regions
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-4 text-[#000000]">Enter New Markets with Confidence</h2>
            <p className="text-lg text-gray-700 mb-4">
              International expansion promises growth but delivers complexity. Most brands underestimate the operational challenges of entering new markets—regulatory compliance, logistics setup, payment processing, local partnerships, and cultural adaptation.
            </p>
            <p className="text-lg text-gray-700">
              We've helped dozens of brands successfully enter new markets by combining strategic planning with hands-on execution. Our framework compresses the typical 12-18 month entry timeline to 90 days while minimizing risk and capital requirements.
            </p>
          </div>
        </div>

        {/* The Motif Unbound Framework */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The 90-Day Market Entry Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Traditional market entry takes 12-18 months and requires significant upfront investment. Our systematic approach gets you operational in 90 days with minimized risk and capital commitment.
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* What Makes Us Different */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">What Makes Us Different</h2>
          <p className="text-lg text-gray-700 mb-8">
            Market entry consultants deliver PowerPoints. We deliver operational market presence—actual infrastructure, partnerships, and sales capability that generates revenue, not recommendations.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Enter Your Next Market?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's discuss your target markets and build a strategic entry plan with minimized risk.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
